import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { userCanEditHomePlan } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import getPageTitle from "lib/utils/getPageTitle";
import { Helmet as Head } from "react-helmet-async";
import useNotification from "ui/hooks/useNotification";
import withAuthentication from "../../../../../../components/withAuthentication";

function HomePlanShowroomPage() {
  const { builderId, homePlanId } = useAppRouterParams();
  const userContextState = useUserContext();

  const { data, error } = useFetchImmutable<ClientHomePlanBaseData>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/base-data`,
    [builderId, homePlanId],
  );

  useNotification("error", error);

  const isMultiUnitHomePlan = !!data?.homePlan?.isMultiUnit; // read-only (aggregated options)
  const overviewOnly = isMultiUnitHomePlan || !userCanEditHomePlan(userContextState, data?.homePlan, data?.community);
  const pageTitle = isMultiUnitHomePlan ? `View ${data?.homePlan.planSetId}` : `Configure ${data?.homePlan.planSetId}`;

  return (
    <>
      <Head>
        <title>{getPageTitle(pageTitle)}</title>
        <meta name="robots" content="noindex" />
      </Head>
      {data && (
        <Showroom
          builder={data?.builder}
          location={data?.community}
          lot={data?.lot}
          masterPlan={data?.masterPlan}
          locationPlan={data?.communityPlan}
          homePlan={data?.homePlan}
          brochureSource={data?.brochureSource}
          lotDiscounts={data?.lotDiscounts}
          homePlanDiscounts={data?.homePlanDiscounts}
          reportingUnitLots={data?.reportingUnitLots}
          overviewOnly={overviewOnly}
          elevationOverrides={data?.elevationOverrides}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/builders/$builder_id/home-plans/$home_plan_id/showroom/")({
  beforeLoad: ({ context, params }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    throw redirect({
      to: "/showroom/plan/$home_plan_id",
      params: { home_plan_id: params.home_plan_id },
      search: { builderId: params.builder_id },
      replace: true,
    });
  },
  component: withAuthentication(HomePlanShowroomPage, {
    permissions: ["VIEW_HOME_PLANS"],
    products: ["showroom"],
  }),
});
